import { useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Save, X } from "lucide-react";
import { useContent, imgSrc } from "../context/ContentContext";
import { useSaveContent } from "./AdminLayout";
import { Card, Text, Area, Select, Lines, ImageField, btnPrimary, btnGhost, slugify } from "./fields";

const emptyProject = (category) => ({
  slug: "",
  title: "",
  category,
  location: "Opmeer en omgeving",
  image: { src: "", alt: "" },
  gallery: [],
  summary: "",
  body: [],
  work: [],
});

const ProjectEditor = ({ initial, categories, onCancel, onSave, saving }) => {
  const [p, setP] = useState(initial);
  const set = (key) => (v) => setP((prev) => ({ ...prev, [key]: v }));
  const setGallery = (i, img) => setP((prev) => ({ ...prev, gallery: prev.gallery.map((g, idx) => (idx === i ? img : g)) }));

  const submit = (e) => {
    e.preventDefault();
    if (!p.title.trim()) return;
    onSave({ ...p, slug: p.slug || slugify(p.title), gallery: (p.gallery || []).filter((g) => g.src) });
  };

  return (
    <form onSubmit={submit} data-testid="project-editor" className="space-y-4">
      <Card
        title={initial.title ? `Project bewerken: ${initial.title}` : "Nieuw project"}
        actions={<button type="button" onClick={onCancel} className={btnGhost}><X className="h-4 w-4" /> Annuleren</button>}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Titel" value={p.title} onChange={(v) => setP((prev) => ({ ...prev, title: v, slug: prev.slug && initial.title ? prev.slug : slugify(v) }))} testid="project-title" />
          <Text label="URL-naam (slug)" value={p.slug} onChange={set("slug")} testid="project-slug" />
          <Select label="Categorie (dienst)" value={p.category} onChange={set("category")} options={categories} testid="project-category" />
          <Text label="Locatie" value={p.location} onChange={set("location")} testid="project-location" />
        </div>
        <Area label="Korte samenvatting" value={p.summary} onChange={set("summary")} rows={2} testid="project-summary" />
        <Area label="Projecttekst" value={(p.body || []).join("\n\n")} onChange={(v) => set("body")(v.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean))} rows={7} testid="project-body" hint="Gebruik een lege regel tussen alinea's." />
        <Lines label="Werkzaamheden" value={p.work} onChange={set("work")} testid="project-work" />
        <ImageField label="Hoofdfoto" value={p.image} onChange={set("image")} testid="project-image-upload" />
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="font-display text-[11px] font-bold uppercase tracking-[0.15em] text-[#555555]">Extra foto's</span>
            <button type="button" onClick={() => setP((prev) => ({ ...prev, gallery: [...(prev.gallery || []), { src: "", alt: "" }] }))} className={btnGhost}><Plus className="h-3.5 w-3.5" /> Foto toevoegen</button>
          </div>
          <div className="space-y-3">
            {(p.gallery || []).map((g, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="flex-1"><ImageField label={`Foto ${i + 1}`} value={g} onChange={(img) => setGallery(i, img)} testid={`project-gallery-upload-${i}`} /></div>
                <button type="button" aria-label="Verwijderen" onClick={() => setP((prev) => ({ ...prev, gallery: prev.gallery.filter((_, idx) => idx !== i) }))} className="mt-6 text-[#8E8D8A] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#E2E0D9] pt-4">
          <button type="submit" data-testid="project-save" disabled={saving} className={btnPrimary}><Save className="h-4 w-4" /> {saving ? "Opslaan..." : "Project opslaan"}</button>
        </div>
      </Card>
    </form>
  );
};

export default function AdminProjecten() {
  const { projects, services } = useContent();
  const { save, saving } = useSaveContent();
  const [editing, setEditing] = useState(null); // index of 'new'
  const categories = services.map((s) => s.title);

  const persist = async (list) => {
    const ok = await save({ projects: list });
    if (ok) setEditing(null);
  };
  const onSave = (p) => {
    const list = editing === "new" ? [...projects, p] : projects.map((it, i) => (i === editing ? p : it));
    persist(list);
  };
  const remove = (i) => {
    if (window.confirm(`Project "${projects[i].title}" verwijderen?`)) persist(projects.filter((_, idx) => idx !== i));
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= projects.length) return;
    const list = [...projects];
    [list[i], list[j]] = [list[j], list[i]];
    persist(list);
  };

  return (
    <div data-testid="admin-projecten-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-wide text-[#111111]">Projecten</h1>
          <p className="mt-1 text-sm text-[#555555]">{projects.length} projecten in het portfolio</p>
        </div>
        {editing === null && (
          <button type="button" data-testid="project-new" onClick={() => setEditing("new")} className={btnPrimary}><Plus className="h-4 w-4" /> Nieuw project</button>
        )}
      </div>

      {editing !== null && (
        <ProjectEditor
          key={editing}
          initial={editing === "new" ? emptyProject(categories[0]) : projects[editing]}
          categories={categories}
          onCancel={() => setEditing(null)}
          onSave={onSave}
          saving={saving}
        />
      )}

      <div className="space-y-2">
        {projects.map((p, i) => (
          <div key={p.slug} data-testid={`project-row-${p.slug}`} className="flex items-center gap-4 border border-[#DDD9D0] bg-white p-3">
            <div className="h-14 w-20 shrink-0 overflow-hidden bg-[#F5F4F1]">
              {p.image?.src && <img src={imgSrc(p.image.src)} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-base font-bold uppercase tracking-wide text-[#111111]">{p.title}</p>
              <p className="text-xs text-[#8E8D8A]">{p.category} · /portfolio/{p.slug}</p>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" aria-label="Omhoog" onClick={() => move(i, -1)} className="p-1.5 text-[#8E8D8A] hover:text-[#111111]"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" aria-label="Omlaag" onClick={() => move(i, 1)} className="p-1.5 text-[#8E8D8A] hover:text-[#111111]"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" data-testid={`project-edit-${p.slug}`} onClick={() => setEditing(i)} className="p-1.5 text-[#8E8D8A] hover:text-[#111111]"><Pencil className="h-4 w-4" /></button>
              <button type="button" data-testid={`project-delete-${p.slug}`} onClick={() => remove(i)} className="p-1.5 text-[#8E8D8A] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
